import type { ConsoleConfig } from './api';

/**
 * A site's two hosts (moodco/headless#265), built from the suffixes
 * `GET /v1/console/config` hands over — the same values the API builds
 * `publicHost()` / `previewHost()` from (api/src/drafts.mjs), so the
 * console never hardcodes a domain. The fallbacks below are the API's
 * own production defaults and are used only until that call answers.
 *
 *   PUBLIC   <key>.<client>.headless.moodco.io — the CNAME target and the
 *            pre-cutover site; once live it 301s to the real domain.
 *            Shown, never opened from here.
 *   PREVIEW  <key>.<client>.headless-preview.moodco.io — where drafts,
 *            the customiser frame, shared links and admin sessions
 *            render. It refuses an anonymous page request, so NOTHING in
 *            the console links to it bare: every entry goes through
 *            `admin/open` (a top-level link) or the bridge with a grant
 *            (the customiser frame).
 */
export const DEFAULT_PUBLIC_HOST_SUFFIX = 'headless.moodco.io';
export const DEFAULT_PREVIEW_HOST_SUFFIX = 'headless-preview.moodco.io';

type Suffixes = Pick<ConsoleConfig, 'public_host_suffix' | 'preview_host_suffix'> | null | undefined;

export function publicHost(config: Suffixes, client: string, key: string): string {
  return `${key}.${client}.${config?.public_host_suffix || DEFAULT_PUBLIC_HOST_SUFFIX}`;
}
export function previewHost(config: Suffixes, client: string, key: string): string {
  return `${key}.${client}.${config?.preview_host_suffix || DEFAULT_PREVIEW_HOST_SUFFIX}`;
}
/** The preview host as an origin — what the customiser frames and checks postMessage against. */
export function previewOrigin(config: Suffixes, client: string, key: string): string {
  return `https://${previewHost(config, client, key)}`;
}

/**
 * Only ever a path on the site. The API's `admin/open` applies the same
 * rule before it builds a redirect, and the product's entry routes
 * resolve `to` against their own origin — this is the first of three
 * gates, not the only one. `//host` is a URL; a backslash folds into a
 * slash in the WHATWG parser, so `/\evil.test` would too.
 */
export function safeSitePath(raw: string | null | undefined): string {
  return typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') && !raw.includes('\\') ? raw : '/';
}

/**
 * The API route that opens a site as the person you are: mints the right
 * grant (admin, or a viewer grant for an editor) and 302s to the site's
 * entry route. A plain same-origin link, so middle-click and open-in-new-
 * tab keep working and the session cookie rides along.
 */
export function adminOpenHref(client: string, key: string, { target = 'preview', to = '/' }: { target?: 'preview' | 'live'; to?: string } = {}): string {
  return `/v1/clients/${encodeURIComponent(client)}/sites/${encodeURIComponent(key)}/admin/open?target=${target}&to=${encodeURIComponent(safeSitePath(to))}`;
}
